<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import { useToast } from "vue-toast-notification";
import { useCommentAttachment } from "@/components/MyStudents/Submissions/composables/useCommentAttachment";
import { useUpsertTodoComment } from "@/components/MyStudents/Submissions/composables/useUpsertTodoComment";
import { ERROR_TIMEOUT } from "@/constants.js";
import { FILE_SIZE_HINT } from "../constants";
import type { CommentComposerProps, TiptapDoc } from "../types";
import { hasCommentBody, toCommentBodyPayload } from "../utils";
import AttachButton from "./AttachButton.vue";
import AttachmentChip from "./AttachmentChip.vue";
import AttachmentError from "./AttachmentError.vue";
import CommentEditor from "./CommentEditor.vue";

const props = defineProps<CommentComposerProps>();
const { applicationId, todoId } = toRefs(props);

const toast = useToast();

const body = ref<TiptapDoc | undefined>();
const { attachment, error, isUploading, selectFiles, removeAttachment, reset, resolveFileUpload } =
  useCommentAttachment();
const { mutateAsync: upsertComment, isPending } = useUpsertTodoComment();

const isSubmitting = computed<boolean>(() => isUploading.value || isPending.value);
const canPost = computed<boolean>(() => (hasCommentBody(body.value) || !!attachment.value) && !isSubmitting.value);

const postComment = async (): Promise<void> => {
  try {
    const fileUpload = await resolveFileUpload();
    await upsertComment({
      applicationId: applicationId.value,
      todoId: todoId.value,
      payload: { comment_body: toCommentBodyPayload(body.value), file_upload: fileUpload },
    });
    body.value = undefined;
    reset();
  } catch {
    toast.open({
      message: "Error while posting the comment. Please, try it later.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};
</script>

<template>
  <CommentEditor v-model="body" placeholder="Add a comment or note for your team…" class="mt-3.5" data-testid="internal-comment-composer">
    <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 py-2.5 border-t border-gray-175 bg-blue-100">
      <AttachButton :disabled="!!attachment" @select="selectFiles" />
      <span class="shrink-0 whitespace-nowrap text-11.5p font-medium text-gray-600" :class="{ 'mr-auto': !attachment }">
        {{ FILE_SIZE_HINT }}
      </span>
      <div v-if="attachment" class="flex min-w-0 max-w-full">
        <AttachmentChip :name="attachment.name" removable @remove="removeAttachment" />
      </div>
      <button
        type="button"
        class="shrink-0 ml-auto h-[34px] px-[18px] rounded-md border border-teal-900 bg-teal-900 text-12.5p font-bold tracking-[0.3px] text-white transition-colors duration-100 hover:bg-[#006667] disabled:cursor-not-allowed disabled:border-gray-175 disabled:bg-gray-175 disabled:text-gray-600"
        :disabled="!canPost"
        data-testid="internal-comment-post-button"
        @click="postComment"
      >
        {{ isSubmitting ? "Posting…" : "Post comment" }}
      </button>
    </div>
    <AttachmentError :message="error" class="mx-3 mb-3" />
  </CommentEditor>
</template>
